import {
	AlertCircle,
	Check,
	Copy,
	FileText,
	Loader2,
	Search,
} from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";

// Skeleton Loader
export const SkeletonLoader = ({ count = 5, columns = 4 }) => (
	<div className="overflow-x-auto">
		<table className="w-full text-sm" aria-busy="true">
			<thead className="bg-slate-800/50 border-b border-slate-700">
				<tr>
					{Array(columns + 1)
						.fill(0)
						.map((_, i) => (
							<th key={`skeleton-th-${i}`} className="px-6 py-3">
								<div className="h-4 bg-slate-700/50 rounded w-20 animate-pulse"></div>
							</th>
						))}
				</tr>
			</thead>
			<tbody className="divide-y divide-slate-700">
				{Array(count)
					.fill(0)
					.map((_, rowIdx) => (
						<tr key={`skeleton-row-${rowIdx}`}>
							{Array(columns + 1)
								.fill(0)
								.map((_item, colIdx) => (
									<td key={`skeleton-col-${colIdx}`} className="px-6 py-4">
										<div className="h-4 bg-slate-700/50 rounded w-32 animate-pulse"></div>
									</td>
								))}
						</tr>
					))}
			</tbody>
		</table>
	</div>
);

SkeletonLoader.propTypes = {
	count: PropTypes.number,
	columns: PropTypes.number,
};

// Table
export const Table = ({
	columns,
	data,
	onEdit,
	onDelete,
	actions,
	loading = false,
}) => {
	if (loading) {
		return <SkeletonLoader count={5} columns={columns.length} />;
	}

	if (data.length === 0) {
		return <EmptyState message="Nenhum registro encontrado" />;
	}

	const hasActions = onEdit || onDelete || actions;

	return (
		<div className="overflow-x-auto">
			<table className="w-full text-sm">
				<thead className="bg-slate-800/50 border-b border-slate-700">
					<tr>
						{columns.map((col) => (
							<th
								key={col.key}
								className="px-6 py-3 text-left font-medium text-slate-300 whitespace-nowrap"
							>
								{col.label}
							</th>
						))}
						{hasActions && (
							<th className="px-6 py-3 text-left font-medium text-slate-300">
								Ações
							</th>
						)}
					</tr>
				</thead>
				<tbody className="divide-y divide-slate-700">
					{data.map((row, idx) => (
						<tr
							key={row.id || `row-${idx}`}
							className="hover:bg-slate-800/30 transition-all"
						>
							{columns.map((col) => (
								<td
									key={`${row.id || idx}-${col.key}`}
									className="px-6 py-4 text-slate-300"
								>
									{col.render ? col.render(row[col.key], row) : row[col.key]}
								</td>
							))}
							{hasActions && (
								<td className="px-6 py-4">
									<div className="flex gap-2 flex-wrap">
										{actions?.(row)}
										{onEdit && (
											<button
												type="button"
												onClick={() => onEdit(row)}
												className="px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded text-xs transition-all active:scale-95"
											>
												Editar
											</button>
										)}
										{onDelete && (
											<button
												type="button"
												onClick={() => onDelete(row.id)}
												className="px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded text-xs transition-all active:scale-95"
											>
												Deletar
											</button>
										)}
									</div>
								</td>
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

Table.propTypes = {
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			render: PropTypes.func,
		}),
	).isRequired,
	data: PropTypes.array.isRequired,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	actions: PropTypes.func,
	loading: PropTypes.bool,
};

// Modal (increased max-h)
export const Modal = ({
	isOpen,
	title,
	children,
	onClose,
	onSubmit,
	submitLabel = "Salvar",
	loading = false,
	size = "md",
}) => {
	if (!isOpen) return null;

	const sizes = {
		sm: "max-w-sm",
		md: "max-w-md",
		lg: "max-w-2xl",
		xl: "max-w-4xl",
		"2xl": "max-w-7xl",
	};

	return (
		<div
			className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
			role="dialog"
			aria-modal="true"
			aria-labelledby="modal-title"
		>
			<div
				className={`bg-slate-900 border border-slate-700 rounded-xl ${sizes[size]} w-full max-h-[85vh] flex flex-col shadow-2xl shadow-black/50`}
			>
				<div className="flex-shrink-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
					<h2 id="modal-title" className="text-lg font-semibold text-white">
						{title}
					</h2>
					<button
						type="button"
						onClick={onClose}
						disabled={loading}
						className="text-slate-400 hover:text-white transition-colors disabled:opacity-50"
						aria-label="Fechar"
					>
						✕
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-6">{children}</div>

				{onSubmit && (
					<div className="flex-shrink-0 bg-slate-800 border-t border-slate-700 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
						<button
							type="button"
							onClick={onClose}
							disabled={loading}
							className="px-4 py-2 text-slate-300 hover:text-white transition-colors disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button type="button" onClick={onSubmit} loading={loading}>
							{submitLabel}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

// Button with spinner
export const Button = ({
	children,
	variant = "primary",
	loading = false,
	size = "md",
	type = "button",
	...props
}) => {
	const variants = {
		primary:
			"bg-cyan-600 hover:bg-cyan-700 text-white hover:shadow-lg hover:shadow-cyan-500/20",
		secondary:
			"bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700",
		danger:
			"bg-red-600 hover:bg-red-700 text-white hover:shadow-lg hover:shadow-red-500/20",
		success:
			"bg-green-600 hover:bg-green-700 text-white hover:shadow-lg hover:shadow-green-500/20",
		ghost: "bg-transparent hover:bg-slate-800 text-slate-400 hover:text-white",
	};

	const sizes = {
		xs: "px-2 py-1 text-[10px]",
		sm: "px-3 py-1.5 text-xs",
		md: "px-4 py-2 text-sm",
		lg: "px-6 py-3 text-base",
	};

	return (
		<button
			type={type}
			{...props}
			disabled={loading || props.disabled}
			className={`rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 inline-flex items-center justify-center gap-2 ${variants[variant]} ${sizes[size]} ${props.className || ""}`}
		>
			{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
		</button>
	);
};

// Input
export const Input = ({ label, error, id, ...props }) => {
	const inputId =
		id ||
		`input-${label?.replace(/\s/g, "-").toLowerCase() || Math.random().toString(36).substr(2, 9)}`;
	const errorId = `${inputId}-error`;

	return (
		<div className="mb-4">
			{label && (
				<label
					htmlFor={inputId}
					className="block text-sm font-medium text-slate-300 mb-2"
				>
					{label}
				</label>
			)}
			<input
				id={inputId}
				aria-invalid={!!error}
				aria-describedby={error ? errorId : undefined}
				{...props}
				className={`w-full bg-slate-800/50 border rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none transition-all ${
					error
						? "border-red-500 focus:border-red-400 focus:ring-2 focus:ring-red-500/30"
						: "border-slate-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30"
				} ${props.className || ""}`}
			/>
			{error && (
				<p id={errorId} className="text-xs text-red-400 mt-1" role="alert">
					{error}
				</p>
			)}
		</div>
	);
};

// Select
export const Select = ({
	label,
	options,
	error,
	placeholder,
	id,
	...props
}) => {
	const selectId =
		id ||
		`select-${label?.replace(/\s/g, "-").toLowerCase() || Math.random().toString(36).substr(2, 9)}`;
	const errorId = `${selectId}-error`;

	return (
		<div className="mb-4">
			{label && (
				<label
					htmlFor={selectId}
					className="block text-sm font-medium text-slate-300 mb-2"
				>
					{label}
				</label>
			)}
			<select
				id={selectId}
				aria-invalid={!!error}
				aria-describedby={error ? errorId : undefined}
				{...props}
				className={`w-full bg-slate-800/50 border rounded-lg px-4 py-2.5 text-white focus:outline-none transition-all ${
					error
						? "border-red-500 focus:border-red-400 focus:ring-2 focus:ring-red-500/30"
						: "border-slate-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30"
				} ${props.className || ""}`}
			>
				<option value="">{placeholder || "Selecione uma opção"}</option>
				{options.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
			{error && (
				<p id={errorId} className="text-xs text-red-400 mt-1" role="alert">
					{error}
				</p>
			)}
		</div>
	);
};

// TextArea
export const TextArea = ({ label, error, id, ...props }) => {
	const textareaId =
		id ||
		`textarea-${label?.replace(/\s/g, "-").toLowerCase() || Math.random().toString(36).substr(2, 9)}`;
	const errorId = `${textareaId}-error`;

	return (
		<div className="mb-4">
			{label && (
				<label
					htmlFor={textareaId}
					className="block text-sm font-medium text-slate-300 mb-2"
				>
					{label}
				</label>
			)}
			<textarea
				id={textareaId}
				aria-invalid={!!error}
				aria-describedby={error ? errorId : undefined}
				{...props}
				className={`w-full bg-slate-800/50 border rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none transition-all resize-none ${
					error
						? "border-red-500 focus:border-red-400 focus:ring-2 focus:ring-red-500/30"
						: "border-slate-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30"
				} ${props.className || ""}`}
			/>
			{error && (
				<p id={errorId} className="text-xs text-red-400 mt-1" role="alert">
					{error}
				</p>
			)}
		</div>
	);
};

// Badge
export const Badge = ({ children, variant = "default" }) => {
	const variants = {
		default: "bg-slate-600/20 text-slate-300 border-slate-500/30",
		pending: "bg-amber-600/20 text-amber-300 border-amber-500/30",
		accepted: "bg-green-600/20 text-green-300 border-green-500/30",
		rejected: "bg-red-600/20 text-red-300 border-red-500/30",
		info: "bg-blue-600/20 text-blue-300 border-blue-500/30",
		cyan: "bg-cyan-600/20 text-cyan-300 border-cyan-500/30",
		purple: "bg-purple-600/20 text-purple-300 border-purple-500/30",
	};

	return (
		<span
			className={`px-3 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1 ${variants[variant]}`}
		>
			{children}
		</span>
	);
};

// Tabs
export const Tabs = ({ tabs, active, onChange }) => (
	<div
		className="flex gap-1 bg-slate-800/50 rounded-lg p-1 border border-slate-700/50"
		role="tablist"
	>
		{tabs.map((tab) => (
			<button
				key={tab.id || tab.value}
				type="button"
				onClick={() => onChange(tab.value)}
				role="tab"
				aria-selected={active === tab.value}
				className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
					active === tab.value
						? "bg-cyan-600/20 text-cyan-300 border border-cyan-500/30"
						: "text-slate-400 hover:text-slate-300 hover:bg-slate-700/30 border border-transparent"
				}`}
			>
				{tab.label}
				{tab.count !== undefined && (
					<span className="ml-2 text-xs opacity-70">({tab.count})</span>
				)}
			</button>
		))}
	</div>
);

// SearchInput
export const SearchInput = ({ value, onChange, placeholder = "Buscar..." }) => (
	<div className="relative">
		<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
		<input
			type="text"
			value={value}
			onChange={(e) => onChange(e.target.value)}
			placeholder={placeholder}
			className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition-all"
		/>
	</div>
);

// CopyButton
export const CopyButton = ({ text, label = "Copiar", size = "sm" }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Button
			variant={copied ? "success" : "secondary"}
			size={size}
			onClick={handleCopy}
			type="button"
		>
			{copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
			{copied ? "Copiado!" : label}
		</Button>
	);
};

// EmptyState
export const EmptyState = ({
	icon: Icon = FileText,
	message = "Nenhum registro encontrado",
	description,
}) => (
	<div className="flex flex-col items-center justify-center py-16 text-center">
		<div className="w-16 h-16 bg-slate-800/50 rounded-2xl flex items-center justify-center mb-4">
			<Icon className="w-8 h-8 text-slate-500" />
		</div>
		<p className="text-slate-400 font-medium">{message}</p>
		{description && (
			<p className="text-slate-500 text-sm mt-1">{description}</p>
		)}
	</div>
);

// Timeline
export const Timeline = ({ items }) => (
	<div className="space-y-0">
		{items.map((item, idx) => (
			<div key={`timeline-${idx}`} className="flex gap-4">
				<div className="flex flex-col items-center">
					<div
						className={`w-3 h-3 rounded-full flex-shrink-0 ${item.active ? "bg-cyan-500" : item.completed ? "bg-green-500" : "bg-slate-600"}`}
					/>
					{idx < items.length - 1 && (
						<div
							className={`w-0.5 flex-1 min-h-[24px] ${item.completed ? "bg-green-500/30" : "bg-slate-700"}`}
						/>
					)}
				</div>
				<div className="pb-4 -mt-0.5">
					<p
						className={`text-sm font-medium ${item.active ? "text-cyan-300" : item.completed ? "text-green-300" : "text-slate-400"}`}
					>
						{item.label}
					</p>
					{item.date && (
						<p className="text-xs text-slate-500 mt-0.5">{item.date}</p>
					)}
				</div>
			</div>
		))}
	</div>
);

// StatCard
export const StatCard = ({
	icon: Icon,
	label,
	value,
	color = "bg-cyan-600/20",
	textColor = "text-cyan-400",
	subtitle,
}) => (
	<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 hover:border-white/10 transition-all">
		<div className="flex items-center justify-between">
			<div>
				<p className="text-slate-400 text-sm mb-1">{label}</p>
				<p className="text-3xl font-bold text-white">{value}</p>
				{subtitle && <p className={`text-xs mt-1 ${textColor}`}>{subtitle}</p>}
			</div>
			<div className={`p-3 rounded-xl ${color}`}>
				<Icon className={`w-6 h-6 ${textColor}`} />
			</div>
		</div>
	</div>
);

// Notification Toast (use at page level)
export const Notification = ({ notification }) => {
	if (!notification) return null;

	return (
		<div
			className={`fixed top-4 right-4 p-4 rounded-lg flex items-center gap-3 shadow-xl z-50 max-w-sm ${
				notification.type === "success"
					? "bg-green-600/20 border border-green-500/30 text-green-300 backdrop-blur-xl"
					: "bg-red-600/20 border border-red-500/30 text-red-300 backdrop-blur-xl"
			}`}
			role="alert"
		>
			{notification.type === "success" ? (
				<Check className="w-5 h-5 flex-shrink-0" />
			) : (
				<AlertCircle className="w-5 h-5 flex-shrink-0" />
			)}
			<span className="text-sm">{notification.message}</span>
		</div>
	);
};

// useNotification hook helper
export const useNotification = () => {
	const [notification, setNotification] = useState(null);

	const showNotification = useCallback((message, type = "success") => {
		setNotification({ message, type });
		setTimeout(() => setNotification(null), 3000);
	}, []);

	return { notification, showNotification };
};

// ============================================
// PropTypes Definitions
// ============================================

Modal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	title: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func,
	submitLabel: PropTypes.string,
	loading: PropTypes.bool,
	size: PropTypes.oneOf(["sm", "md", "lg", "xl", "2xl"]),
};

Button.propTypes = {
	children: PropTypes.node.isRequired,
	variant: PropTypes.oneOf([
		"primary",
		"secondary",
		"danger",
		"success",
		"ghost",
	]),
	loading: PropTypes.bool,
	size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
	onClick: PropTypes.func,
	disabled: PropTypes.bool,
	className: PropTypes.string,
	type: PropTypes.string,
};

Input.propTypes = {
	label: PropTypes.string,
	error: PropTypes.string,
	id: PropTypes.string,
	className: PropTypes.string,
};

Select.propTypes = {
	label: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
				.isRequired,
			label: PropTypes.string.isRequired,
		}),
	).isRequired,
	error: PropTypes.string,
	placeholder: PropTypes.string,
	id: PropTypes.string,
	className: PropTypes.string,
};

TextArea.propTypes = {
	label: PropTypes.string,
	error: PropTypes.string,
	id: PropTypes.string,
	className: PropTypes.string,
};

Badge.propTypes = {
	children: PropTypes.node.isRequired,
	variant: PropTypes.oneOf([
		"default",
		"pending",
		"accepted",
		"rejected",
		"info",
		"cyan",
		"purple",
	]),
};

Tabs.propTypes = {
	tabs: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			id: PropTypes.string,
			label: PropTypes.string.isRequired,
			count: PropTypes.number,
		}),
	).isRequired,
	active: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
};

SearchInput.propTypes = {
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
};

CopyButton.propTypes = {
	text: PropTypes.string.isRequired,
	label: PropTypes.string,
	size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
};

EmptyState.propTypes = {
	icon: PropTypes.elementType,
	message: PropTypes.string,
	description: PropTypes.string,
};

Timeline.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			date: PropTypes.string,
			active: PropTypes.bool,
			completed: PropTypes.bool,
		}),
	).isRequired,
};

StatCard.propTypes = {
	icon: PropTypes.elementType.isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	color: PropTypes.string,
	textColor: PropTypes.string,
	subtitle: PropTypes.string,
};

Notification.propTypes = {
	notification: PropTypes.shape({
		message: PropTypes.string.isRequired,
		type: PropTypes.oneOf(["success", "error"]).isRequired,
	}),
};
